import { Typography, Link as MuiLink, Box, Grid, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useLocation, useNavigate } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import { Trans } from 'react-i18next';
import { ResponsiveLayout } from '../components/ResponsiveLayout';
import { GREEN_LOGO, GREEN_MAIN, WHITE, BLACK, GRAY_MAIN } from '../theme/colors';
import settings from '../utilities/settings';
import { getFromLocalStorage } from '../helpers/localStorage';
import { useTranslationContext } from '../hooks/translations/translationsProvider';

/** Parole chiave headline: più grandi del corpo, grassetto, colore logo; allineate in baseline col resto della riga. */
const homeRegisterHeadlineStrong = (
    <Box
        component="strong"
        sx={{
            display: 'inline',
            fontWeight: 700,
            fontSize: '1.52em',
            lineHeight: 1,
            verticalAlign: 'baseline',
            color: GREEN_LOGO,
        }}
    />
);

const HomeRegister = () => {
    const auth = useAuth();
    const navigate = useNavigate();
    const [url, setUrl] = useState<string>('');
    const location = useLocation();
    const ReturnUrl = settings.Domains.ServiceClient + "/company/register/callback"
    let referral: string | undefined = getFromLocalStorage("Referral");
    const { t } = useTranslationContext();
    const theme = useTheme();

    const titleSize = isMobile ? "1.4rem" : "1.3rem"
    const bodySize = isMobile ? "1.05rem" : "1.15rem"
    const buttonSpacing = isMobile ? 1.5 : 2
    const headlineSize = isMobile ? "2.35rem" : "3.15rem"
    const subtitleSize = isMobile ? "0.72rem" : "0.82rem"

    useEffect(() => {
        if (auth.isAuthenticated)
            navigate('/home')
    }, [auth.isAuthenticated])

    useEffect(() => {
        setUrl(`${window.location.protocol}//${window.location.host}${location.pathname}${location.search}`)
    }, [location])

    const buttonSx = {
        bgcolor: GREEN_LOGO,
        "&:hover": { bgcolor: "darkgreen" },
        borderRadius: 10,
        px: { xs: 2, sm: 3 },
        py: { xs: 1.25, sm: 1.15 },
        fontSize: isMobile ? "0.92rem" : "0.75rem",
        lineHeight: 1.15,
        textTransform: "none" as const,
        fontWeight: "bold" as const,
        color: WHITE,
        border: "2px solid " + GREEN_LOGO,
        whiteSpace: "nowrap" as const,
        width: { xs: "100%", md: "auto" },
    }

    const ImageContent = () => (
        <Box>
            <Box
                component="img"
                sx={{
                    width: "100%",
                    height: "auto",
                    borderRadius: 2,
                    display: "block",
                    [theme.breakpoints.up('md')]: {
                        transform: 'scale(1.1)',
                        transformOrigin: 'right center',
                    },
                }}
                alt=""
                src="/Partecipa_privato.jpg"
            />
            <Box
                sx={{
                    mt: { xs: 3.5, md: 4 },
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block',
                        width: '100%',
                        color: GRAY_MAIN,
                        textTransform: 'lowercase',
                        letterSpacing: 0.6,
                        mb: 1.25,
                        fontSize: '0.675rem',
                        fontStyle: 'italic',
                    }}
                >
                    {t("HomeRegister.WePower")}
                </Typography>
                <Box
                    component="img"
                    src="/assoBayLogo.png"
                    alt="ASSOBAY"
                    sx={{
                        height: { xs: 44, sm: 52, md: 56 },
                        width: 'auto',
                        maxWidth: '100%',
                        display: 'block',
                        mb: 2.25,
                        mx: 'auto',
                    }}
                />
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1.5,
                        rowGap: 1,
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    <MuiLink
                        component="a"
                        href={settings.InfosWebsiteAssoBay.Url}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        sx={{
                            color: GRAY_MAIN,
                            fontSize: { xs: '0.9rem', md: '0.95rem' },
                        }}
                    >
                        {t("HomeRegister.LinkWhatIsAssobay")}
                    </MuiLink>
                    <Typography component="span" sx={{ color: GRAY_MAIN, fontSize: '0.9rem' }}>|</Typography>
                    <MuiLink
                        component="a"
                        href={settings.InfosWebsiteAssoBay.Url + '/it/content/17-gli-asso?from=landing'}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        sx={{
                            color: GRAY_MAIN,
                            fontSize: { xs: '0.9rem', md: '0.95rem' },
                        }}
                    >
                        {t("GLI ASSO")}
                    </MuiLink>
                </Box>
            </Box>
        </Box>
    )

    const TextContent = () => (
        <Box
            sx={{
                color: "darkgreen",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignSelf: "stretch",
                textAlign: { xs: "center", md: "center" },
                px: { xs: 0, md: 1 },
                py: { xs: 1, md: 0 },
                width: "100%",
            }}
        >
            <Typography
                variant="caption"
                sx={{
                    display: 'block',
                    color: GRAY_MAIN,
                    textTransform: 'uppercase',
                    letterSpacing: { xs: 1, md: 1.4 },
                    fontSize: subtitleSize,
                    mb: { xs: 4, md: 5 },
                    maxWidth: '36rem',
                    mx: 'auto',
                }}
            >
                {t("HomeRegister.IdentitySubtitle")}
            </Typography>

            <Box
                sx={{
                    mb: { xs: 5, md: 6 },
                    maxWidth: '38rem',
                    mx: 'auto',
                    width: '100%',
                    color: BLACK,
                }}
            >
                <Typography
                    component="h1"
                    sx={{
                        fontSize: headlineSize,
                        fontWeight: 400,
                        lineHeight: 1.35,
                        color: 'inherit',
                        textTransform: 'uppercase',
                        letterSpacing: '0.02em',
                        mb: 1.5,
                    }}
                >
                    <Trans
                        i18nKey="HomeRegister.HeadlineLine1"
                        components={{ strong: homeRegisterHeadlineStrong }}
                    />
                </Typography>
                <Typography
                    component="h1"
                    sx={{
                        fontSize: headlineSize,
                        fontWeight: 400,
                        lineHeight: 1.35,
                        color: 'inherit',
                        textTransform: 'uppercase',
                        letterSpacing: '0.02em',
                    }}
                >
                    <Trans
                        i18nKey="HomeRegister.HeadlineLine2"
                        components={{ strong: homeRegisterHeadlineStrong }}
                    />
                </Typography>
            </Box>

            <Typography
                variant="body1"
                paragraph
                sx={{
                    fontSize: bodySize,
                    mb: { xs: 6, md: 7 },
                    textAlign: "center",
                    color: BLACK,
                    maxWidth: '34rem',
                    mx: 'auto',
                    lineHeight: 1.7,
                }}
            >
                {t("HomeRegister.Description")}
            </Typography>

            <Typography
                variant="h5"
                sx={{
                    fontSize: `calc(${titleSize} * 0.7)`,
                    fontWeight: "bold",
                    color: 'rgba(0, 0, 0, 0.7)',
                    mb: { xs: 4, md: 4.5 },
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                }}
            >
                {t("HomeRegister.RegisterAsLabel")}
            </Typography>

            <Grid
                container
                spacing={buttonSpacing}
                justifyContent="center"
                direction={isMobile ? "column" : "row"}
                alignItems="stretch"
                sx={{ width: '100%', maxWidth: '100%' }}
            >
                <Grid
                    item
                    xs={12}
                    md="auto"
                    sx={{ display: 'flex', justifyContent: 'center' }}
                >
                    <Button
                        variant="contained"
                        sx={buttonSx}
                        onClick={() => {
                            window.location.href = settings.Domains.IdentityClient + `/account/register?ReturnUrl=${url}`
                        }}
                    >
                        {t("UTENTE PRIVATO")}
                    </Button>
                </Grid>
                <Grid
                    item
                    xs={12}
                    md="auto"
                    sx={{ display: 'flex', justifyContent: 'center' }}
                >
                    <Button
                        variant="contained"
                        sx={buttonSx}
                        onClick={() => {
                            const href = referral
                                ? settings.Domains.IdentityClient + `/account/register-company?ReturnUrl=${ReturnUrl}&Type=1&UserRef=${referral}`
                                : settings.Domains.IdentityClient + `/account/register-company?ReturnUrl=${ReturnUrl}&Type=1`
                            window.location.href = href
                        }}
                    >
                        {t("ASSOCIAZIONE")}
                    </Button>
                </Grid>
                <Grid
                    item
                    xs={12}
                    md="auto"
                    sx={{ display: 'flex', justifyContent: 'center' }}
                >
                    <Button
                        variant="contained"
                        sx={buttonSx}
                        onClick={() => {
                            const href = referral
                                ? settings.Domains.IdentityClient + `/account/register-company?ReturnUrl=${ReturnUrl}&Type=2&UserRef=${referral}`
                                : settings.Domains.IdentityClient + `/account/register-company?ReturnUrl=${ReturnUrl}&Type=2`
                            window.location.href = href
                        }}
                    >
                        {t("ATTIVITA' ECONOMICA")}
                    </Button>
                </Grid>
            </Grid>
        </Box>
    )

    return (
        <>
            <Box sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'flex-start',
            }}>
                <Box sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    flexDirection: 'column',
                }}>
                    <ResponsiveLayout
                        imageLead
                        alignColumnsTop
                        spacing={isMobile ? 3 : 5}
                        imageContent={<ImageContent />}
                        textContent={<TextContent />}
                    />
                </Box>
            </Box>
        </>
    )
}

export default HomeRegister;
